import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, TagIcon as Tag, UserIcon as User, CompassIcon as Compass, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown } from "@phosphor-icons/react";

// Hook: carica le liste configurabili CRM (categorie + commerciali)
export function useCrmConfig() {
  const [cfg, setCfg] = useState({ categorie: [], commerciali: [], origini: [], fornitori_servizi: [], tipologie_servizi: [] });
  useEffect(() => {
    api.get("/crm-config")
      .then(({ data }) => setCfg({ categorie: data.categorie || [], commerciali: data.commerciali || [], origini: data.origini || [], fornitori_servizi: data.fornitori_servizi || [], tipologie_servizi: data.tipologie_servizi || [] }))
      .catch(() => {});
  }, []);
  return cfg;
}

// Select riutilizzabile per categoria/commerciale. Mostra anche eventuali valori
// legacy non presenti nell'elenco, così i record esistenti restano leggibili.
export function CrmSelect({ value, onChange, options, placeholder, testid, emptyLabel = "—", className = "mt-1 rounded-sm" }) {
  const opts = options || [];
  return (
    <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)}>
      <SelectTrigger className={className} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">{emptyLabel}</SelectItem>
        {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        {value && !opts.includes(value) && <SelectItem value={value}>{value} (non in elenco)</SelectItem>}
      </SelectContent>
    </Select>
  );
}

export function EditableList({ title, icon, items, onChange, placeholder, testidPrefix }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("Voce già presente"); return; }
    onChange([...items, v]); setVal("");
  };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">{icon}<h4 className="font-heading font-semibold text-sm">{title}</h4></div>
      <div className="flex gap-2 mb-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="rounded-sm h-9" data-testid={`${testidPrefix}-input`} />
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={add} data-testid={`${testidPrefix}-add`}><Plus size={14} /> Aggiungi</Button>
      </div>
      <div className="space-y-1">
        {items.length === 0 && <p className="text-xs text-muted-foreground">Nessuna voce.</p>}
        {items.map((it, i) => (
          <div key={it} className="flex items-center justify-between border border-border rounded-sm px-2.5 py-1.5" data-testid={`${testidPrefix}-row`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex flex-col shrink-0" title="Sposta">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-default" data-testid={`${testidPrefix}-up-${i}`}><CaretUp size={12} weight="bold" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-default" data-testid={`${testidPrefix}-down-${i}`}><CaretDown size={12} weight="bold" /></button>
              </div>
              <span className="text-sm truncate">{it}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 shrink-0" onClick={() => onChange(items.filter((x) => x !== it))} data-testid={`${testidPrefix}-remove`}><Trash size={14} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sezione impostazioni: gestione elenchi Categoria e Commerciale
export function CrmConfigSection() {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => api.get("/crm-config").then(({ data }) => setCfg({ categorie: data.categorie || [], commerciali: data.commerciali || [], origini: data.origini || [], fornitori_servizi: data.fornitori_servizi || [] })).catch(() => setCfg({ categorie: [], commerciali: [], origini: [], fornitori_servizi: [] })), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put("/crm-config", cfg); toast.success("Elenchi commerciali salvati"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };
  if (!cfg) return <p className="text-sm text-muted-foreground">Caricamento…</p>;
  return (
    <Card className="rounded-sm shadow-none p-4 space-y-5" data-testid="crm-config-section">
      <div>
        <h3 className="font-heading text-base font-semibold">Commerciale — Categorie & Commerciali</h3>
        <p className="text-[11px] text-muted-foreground mt-0.5">Questi elenchi popolano i menu a tendina "Categoria" e "Commerciale" in Opportunità e Preventivi.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <EditableList title="Categorie" icon={<Tag size={16} className="text-primary" />} items={cfg.categorie} onChange={(v) => setCfg((c) => ({ ...c, categorie: v }))} placeholder="es. Hardware, Cloud, Sicurezza" testidPrefix="crm-cat" />
        <EditableList title="Commerciali" icon={<User size={16} className="text-primary" />} items={cfg.commerciali} onChange={(v) => setCfg((c) => ({ ...c, commerciali: v }))} placeholder="es. Mario Rossi" testidPrefix="crm-comm" />
        <EditableList title="Origini" icon={<Compass size={16} className="text-primary" />} items={cfg.origini} onChange={(v) => setCfg((c) => ({ ...c, origini: v }))} placeholder="es. Sito web, Passaparola, Fiera" testidPrefix="crm-orig" />
      </div>
      <div className="pt-2 border-t border-border">
        <h3 className="font-heading text-base font-semibold">Servizi — Fornitori</h3>
        <p className="text-[11px] text-muted-foreground mt-0.5 mb-4">Questo elenco popola il menu a tendina "Fornitore" nella pagina Servizi.</p>
        <div className="grid md:grid-cols-3 gap-6">
          <EditableList title="Fornitori servizi" icon={<Tag size={16} className="text-primary" />} items={cfg.fornitori_servizi} onChange={(v) => setCfg((c) => ({ ...c, fornitori_servizi: v }))} placeholder="es. Aruba, Zyxel Nebula, Sophos" testidPrefix="crm-forn" />
        </div>
      </div>
      <div className="flex justify-end"><Button className="rounded-sm" onClick={save} data-testid="crm-config-save">Salva elenchi</Button></div>
    </Card>
  );
}
